import React, { useState, useContext, useEffect, useRef } from 'react';
import {
	Paper,
	Typography,
	IconButton,
	Tooltip,
	Stack,
	TableContainer,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Table,
	Button,
	Grid,
	Box
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { isAccess, simpleMessage } from '../../../helpers/Helpers';
import { tienePermiso } from '../../../helpers/permisos';
import { formatearMonto } from '../../../helpers/formato';

import { DataContext } from '../../../context/DataContext';
import {
	CargandoTabla,
	EstadoChip,
	EstadoError,
	EstadoVacio,
	KpiCard,
	useAviso
} from '../../../components/base';
import { getCreditPaymentAsync } from '../../../api/Credits';
import { getToken } from '../../../api/Account';
import ReactToPrint from 'react-to-print';
import PrintPayment from './PrintPayment';
import UpdateFechaPago from './paymentComponents/UpdateFechaPago';
import PrintPagare from './paymentComponents/PrintPagare';
import {
	celdaFecha,
	celdaMonto,
	cuotaVencida,
	encabezadoTabla,
	fechaDeCredito
} from '../comunes';

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
const ERROR_OTRA_SESION = 'Sesión activa en otro dispositivo';

// Encabezado de columna con una ayuda para cuando la columna queda angosta.
const Encabezado = ({ children, ayuda, derecha = false }) => (
	<TableCell sx={{ ...encabezadoTabla, textAlign: derecha ? 'right' : 'left' }}>
		<Tooltip title={ayuda} placement="top">
			<span>{children}</span>
		</Tooltip>
	</TableCell>
);

const CreditPayment = ({
	id,
	clientName,
	isNominal,
	dataCredit,
	onPlanCargado
}) => {
	const token = getToken();
	const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
	const aviso = useAviso();

	const [paymentList, setPaymentList] = useState([]);
	const [credit, setCredit] = useState([]);
	const [quoteList, setQuoteList] = useState([]);

	const [montoPagar, setMontoPagar] = useState();

	const [showModal, setShowModal] = useState(false);
	const [selectedPayment, setSelectedPayment] = useState([]);
	const [localReload, setLocalReload] = useState(false);

	// Estado local de la pestaña: esqueleto mientras carga y error con "Reintentar".
	const [cargando, setCargando] = useState(true);
	const [error, setError] = useState(false);

	const cargar = async () => {
		setCargando(true);
		setError(false);
		setIsLoading(true);
		try {
			const result = await getCreditPaymentAsync(token, id);
			if (!result.statusResponse) {
				setIsLoading(false);
				setCargando(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				setError(true);
				if (result.error === ERROR_OTRA_SESION) {
					simpleMessage(result.error, 'error');
				}
				return;
			}
			setPaymentList(result.data.result.payments);
			setCredit(result.data.result);
			setQuoteList(result.data.result.quotes);

			setMontoPagar(result.data.result.montoPagar);

			setIsLoading(false);
			setCargando(false);
		} catch (e) {
			// Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
			setIsLoading(false);
			setCargando(false);
			setError('conexion');
		}
	};

	useEffect(() => {
		cargar();
	}, [localReload]);

	const totalAbonado = () => {
		let result = 0;
		if (quoteList.length > 0) {
			quoteList.map((item) => (result += item.totalRecibido));
		}
		return result;
	};

	// Hay cuotas vencidas sin pagar (solo cuenta si el crédito ya se entregó y sigue vigente).
	const entregadoVigente = credit.isEntregado !== false && !credit.isCanceled;
	const hayMora = entregadoVigente && paymentList.some((cuota) => cuotaVencida(cuota));

	// Saldo del sistema (el "Saldo actual" del cobro): la suma del saldo de capital e interés corriente
	// de las cuotas. Lo usan el resumen del encabezado y el indicador "Saldo pendiente" (decisión del
	// usuario 2026-10-06; antes el indicador era monto a pagar − abonos).
	const saldoPlan = paymentList.reduce(
		(suma, cuota) => suma + cuota.saldoPrincipal + cuota.saldoInteresCorriente,
		0
	);
	const datosListos = !cargando && !error;
	useEffect(() => {
		if (datosListos && onPlanCargado) {
			onPlanCargado({ saldo: saldoPlan, mora: hayMora });
		}
	}, [datosListos, paymentList, hayMora]);

	const compRefPagare = useRef();
	const compRef = useRef();

	const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  @page { size: landscape;  margin: 8mm; }
`;

	const pageStylePagare = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  // @page { size: landscape;  margin: 8mm; }
`;

	const editpayment = async (payment) => {
		setSelectedPayment(payment);
		setShowModal(true);
	};

	// Los botones de imprimir esperan a que lleguen los datos del plan.
	const sinDatos = cargando || Boolean(error) || paymentList.length === 0;
	// La pantalla pedía PAYMENT UPDATE y UpdateFechaPago exige CREDITS CREATE en el servidor: se piden
	// los dos (PAYMENT UPDATE sigue teniendo efecto en el editor de roles y el servidor no responde 401).
	const puedeCambiarFecha =
		tienePermiso(access, 'PAYMENT UPDATE') && tienePermiso(access, 'CREDITS CREATE');

	let contenidoPlan;
	if (cargando) {
		contenidoPlan = (
			<CargandoTabla filas={8} columnas={9} etiqueta="Cargando el plan de pagos…" />
		);
	} else if (error) {
		contenidoPlan = (
			<EstadoError
				titulo={
					error === 'conexion'
						? 'No se pudo conectar con el servidor'
						: 'No se pudo cargar el plan de pagos'
				}
				onReintentar={cargar}
			/>
		);
	} else if (paymentList.length === 0) {
		contenidoPlan = (
			<EstadoVacio
				titulo="Sin plan de pagos"
				descripcion="Este crédito todavía no tiene cuotas en su plan de pagos."
			/>
		);
	} else {
		contenidoPlan = (
			<TableContainer>
				<Table aria-label="Plan de pagos" size="small">
					<TableHead>
						<TableRow>
							<Encabezado ayuda="Número de la cuota en el plan">Cuota</Encabezado>
							<Encabezado ayuda="Días de periodicidad de la cuota" derecha>
								Días
							</Encabezado>
							<Encabezado ayuda="Fecha en que vence la cuota">Fecha de pago</Encabezado>
							<Encabezado ayuda="Saldo antes de pagar esta cuota" derecha>
								Saldo anterior
							</Encabezado>
							<Encabezado ayuda="Parte de la cuota que va al capital" derecha>
								Capital
							</Encabezado>
							<Encabezado ayuda="Parte de la cuota que va a interés corriente" derecha>
								Interés corriente
							</Encabezado>
							<Encabezado ayuda="Total que se debe pagar en esta cuota" derecha>
								Cuota a pagar
							</Encabezado>
							<Encabezado ayuda="Saldo después de pagar esta cuota" derecha>
								Nuevo saldo
							</Encabezado>
							<Encabezado ayuda="Si la cuota está pagada, pendiente o en mora">
								Estado
							</Encabezado>
							{puedeCambiarFecha ? (
								<TableCell sx={{ ...encabezadoTabla, textAlign: 'right' }}>
									Acciones
								</TableCell>
							) : null}
						</TableRow>
					</TableHead>
					<TableBody>
						{paymentList.map((row) => {
							return (
								<TableRow key={row.id} hover>
									<TableCell component="th" scope="row" sx={celdaFecha}>
										{row.numeroCuota}
									</TableCell>
									<TableCell sx={celdaMonto}>{row.periodicidad}</TableCell>
									<TableCell sx={celdaFecha}>{fechaDeCredito(row.fechaPago)}</TableCell>
									<TableCell sx={celdaMonto}>{formatearMonto(row.saldoAnterior)}</TableCell>
									<TableCell sx={celdaMonto}>{formatearMonto(row.montoPrincipal)}</TableCell>
									<TableCell sx={celdaMonto}>
										{formatearMonto(row.montoInteresCorriente)}
									</TableCell>
									<TableCell sx={celdaMonto}>
										{formatearMonto(isNominal ? row.montoInteresCorriente : row.cuotaPrincipal)}
									</TableCell>
									<TableCell sx={celdaMonto}>{formatearMonto(row.nuevoSaldo)}</TableCell>
									<TableCell>
										{row.isCanceled ? (
											<EstadoChip estado="ok" label="Pagada" />
										) : entregadoVigente && cuotaVencida(row) ? (
											<EstadoChip estado="mora" label="En mora" />
										) : (
											<EstadoChip estado="neutro" label="Pendiente" />
										)}
									</TableCell>

									{puedeCambiarFecha ? (
										<TableCell sx={{ textAlign: 'right' }}>
											<Tooltip title="Cambiar la fecha de la cuota">
												<IconButton
													aria-label={`Cambiar la fecha de la cuota ${row.numeroCuota}`}
													sx={{ color: 'primary.main' }}
													onClick={() => editpayment(row)}
												>
													<EventRepeatOutlinedIcon />
												</IconButton>
											</Tooltip>
										</TableCell>
									) : null}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		);
	}

	return (
		<div>
			<Stack direction="column" spacing={3}>
				<Grid container spacing={2} component="section" aria-label="Totales del plan">
					<Grid item xs={12} sm={4}>
						<KpiCard
							titulo="Monto a pagar"
							valor={montoPagar}
							formato="monto"
							icono={<PaymentsOutlinedIcon />}
							tono="primary"
							cargando={cargando}
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<KpiCard
							titulo="Total abonado"
							valor={montoPagar === undefined ? undefined : totalAbonado()}
							formato="monto"
							icono={<CheckCircleOutlineIcon />}
							tono="success"
							cargando={cargando}
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<KpiCard
							titulo="Saldo pendiente"
							valor={montoPagar === undefined ? undefined : saldoPlan}
							formato="monto"
							icono={<AccountBalanceWalletOutlinedIcon />}
							tono="warning"
							cargando={cargando}
						/>
					</Grid>
				</Grid>

				<Paper sx={{ p: { xs: 2, sm: 3 } }}>
					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						alignItems={{ xs: 'flex-start', sm: 'center' }}
						justifyContent={'space-between'}
						spacing={1.5}
						sx={{ mb: 2 }}
					>
						<Typography
							component="h2"
							variant="h6"
							sx={{ fontWeight: 700, color: 'primary.main' }}
						>
							Plan de pagos
						</Typography>

						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
							{isAccess(access, 'PRINT PAGARE') ? (
								<ReactToPrint
									trigger={() => {
										return (
											<Button
												variant="outlined"
												startIcon={<FontAwesomeIcon icon={faPrint} />}
												disabled={sinDatos || !dataCredit}
											>
												Contrato
											</Button>
										);
									}}
									content={() => compRefPagare.current}
									pageStyle={pageStylePagare}
								/>
							) : (
								<></>
							)}

							<ReactToPrint
								trigger={() => {
									return (
										<Button
											variant="outlined"
											startIcon={<FontAwesomeIcon icon={faPrint} />}
											disabled={sinDatos}
										>
											Imprimir plan
										</Button>
									);
								}}
								content={() => compRef.current}
								pageStyle={pageStyle}
							/>
						</Box>
					</Stack>

					{contenidoPlan}
				</Paper>
			</Stack>

			<div style={{ display: 'none' }}>
				<PrintPagare
					clientName={clientName}
					dataCredit={dataCredit}
					paymentList={paymentList}
					ref={compRefPagare}
				/>
			</div>

			<div style={{ display: 'none' }}>
				<PrintPayment
					clientName={clientName}
					credit={credit}
					paymentList={paymentList}
					ref={compRef}
				/>
			</div>

			{showModal ? (
				<UpdateFechaPago
					cuota={selectedPayment}
					plan={paymentList}
					onCerrar={() => setShowModal(false)}
					onGuardado={(mensaje) => {
						setShowModal(false);
						setLocalReload(!localReload);
						aviso.exito(mensaje);
					}}
				/>
			) : null}
		</div>
	);
};

export default CreditPayment;
